/**
 * ArubaManageDevicesPage — fleet-wide Aruba inventory table.
 *
 * Read-only as of 2026-05-06 — the upstream Aruba write surface (rename /
 * group reassign / site reassign) isn't plumbed yet. The page surfaces real
 * data from /api/aruba/devices/ which fans out across APs + switches +
 * gateways + mobility controllers and merges into one flat list.
 *
 * Tenants can be sizable (the test apigw-ca tenant returns 2,320 devices),
 * so the page caps the visible row count at MAX_ROWS and prompts the user
 * to refine the filter rather than rendering thousands of <input>s. Filter
 * chips on device type (ap/switch/gateway/mc) plus a free-text search keep
 * the typical use case (find one device by name or serial) snappy.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Search,
  Wifi,
  Network,
  Router,
  Cpu,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaDevices } from "@/stores/aruba-devices";
import { useAiAgent } from "@/stores/ai-agent";
import type { ArubaDevice } from "@/api/schemas/aruba";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.0fr 0.55fr 0.6fr 1fr 0.85fr 0.9fr 0.85fr 0.45fr";

/** Build a focused diagnostic prompt for an Aruba device. Lets operators
 *  triage one row without typing. */
function arubaDiagnosePrompt(d: ArubaDevice): string {
  const t = d.deviceType ?? "device";
  return (
    `Diagnose Aruba ${t} ${d.serial}` +
    (d.name ? ` "${d.name}"` : "") +
    (d.site ? ` at site "${d.site}"` : "") +
    (d.group ? ` (group ${d.group})` : "") +
    `. Current status: ${d.status ?? "unknown"}. Use the Aruba diagnostic ` +
    `tools to check: device health/uptime via aruba_device_detail; ` +
    (t === "switch" ? "per-port errors via aruba_switch_port_errors; " : "") +
    `recent audit events via aruba_recent_events; ` +
    (t === "ap" ? "failed wireless connections; " : "") +
    `Summarize what's wrong or "all clear".`
  );
}

// Hard cap on visible rows to keep the DOM tractable. With 2,300+ devices on
// a real tenant, rendering everything at once is a 5+ second jank and the
// user can't read it anyway. The "X of Y — refine the filter" hint nudges
// people to search by name/serial which is the common case.
const MAX_ROWS = 200;

const TYPE_META: Record<string, { label: string; Icon: typeof Wifi }> = {
  ap: { label: "AP", Icon: Wifi },
  switch: { label: "Switch", Icon: Network },
  gateway: { label: "Gateway", Icon: Router },
  mc: { label: "MC", Icon: Cpu },
};

function statusBadge(status: string | null | undefined) {
  if (!status)
    return (
      <Badge variant="neutral" size="sm">
        —
      </Badge>
    );
  const lower = status.toLowerCase();
  if (lower === "up" || lower === "connected")
    return (
      <Badge variant="ok" dot>
        {status}
      </Badge>
    );
  if (lower.includes("reboot"))
    return (
      <Badge variant="warn" dot>
        {status}
      </Badge>
    );
  if (lower === "down" || lower === "offline")
    return (
      <Badge variant="err" dot>
        {status}
      </Badge>
    );
  return (
    <Badge variant="info" dot>
      {status}
    </Badge>
  );
}

function typeChip(type: string | null | undefined) {
  if (!type) return null;
  const meta = TYPE_META[type];
  if (!meta)
    return (
      <Badge variant="neutral" size="sm">
        {type}
      </Badge>
    );
  const { Icon, label } = meta;
  return (
    <Badge variant="neutral" size="sm">
      <Icon size={10} />
      {label}
    </Badge>
  );
}

export function ArubaManageDevicesPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useArubaDevices();
  const sendAgent = useAiAgent((s) => s.send);
  const setAgentOpen = useAiAgent((s) => s.setOpen);
  useEffect(() => {
    void load();
  }, [load]);

  function diagnose(d: ArubaDevice) {
    setAgentOpen(true);
    void sendAgent(arubaDiagnosePrompt(d));
  }

  const [query, setQuery] = useState("");
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());

  const filtered = useMemo<ArubaDevice[]>(() => {
    const q = query.trim().toLowerCase();
    return data.filter((r) => {
      if (
        activeTypes.size > 0 &&
        !(r.deviceType && activeTypes.has(r.deviceType))
      ) {
        return false;
      }
      if (!q) return true;
      return (
        (r.name ?? "").toLowerCase().includes(q) ||
        r.serial.toLowerCase().includes(q) ||
        (r.model ?? "").toLowerCase().includes(q) ||
        (r.group ?? "").toLowerCase().includes(q) ||
        (r.site ?? "").toLowerCase().includes(q) ||
        (r.macAddress ?? "").toLowerCase().includes(q) ||
        (r.ipAddress ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, query, activeTypes]);

  // Per-type counts on the filter chips reflect the *unfiltered* dataset so
  // toggling chips never makes the chip's own count drop to zero — that
  // would be confusing.
  const typeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of data) {
      const t = r.deviceType ?? "other";
      c[t] = (c[t] ?? 0) + 1;
    }
    return c;
  }, [data]);

  const visible = filtered.slice(0, MAX_ROWS);
  const overflow = filtered.length - visible.length;

  function toggleType(t: string) {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Manage devices</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Manage devices
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Aruba Central inventory across every site reachable through your
            active key — APs, switches, gateways, and mobility controllers
            merged. Read-only for now; bulk-edit returns once the upstream write
            surface is wired.
          </p>
        </div>
        <Badge variant="info" size="md">
          {data.length} devices
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, serial, model, group, site, IP, MAC…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              type
            </span>
            {(["ap", "switch", "gateway", "mc"] as const).map((t) => {
              const meta = TYPE_META[t];
              const Icon = meta.Icon;
              const on = activeTypes.has(t);
              const n = typeCounts[t] ?? 0;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                    on
                      ? "border-violet/40 bg-aurora-soft text-violet"
                      : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                  )}
                  disabled={n === 0}
                  title={n === 0 ? `No ${meta.label}s in inventory` : undefined}
                >
                  <Icon size={11} />
                  {meta.label}
                  <span className="ml-0.5 font-mono text-[9.5px] opacity-70">
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
          <span className="ml-auto font-mono text-xs text-fg-mute">
            {filtered.length}/{data.length} match
            {overflow > 0 ? <> · capped to first {MAX_ROWS}</> : null}
          </span>
        </CardContent>
      </Card>

      {status === "loading" && data.length === 0 ? (
        <TableSkeleton columns={COLS} columnCount={8} rows={6} />
      ) : (
        <TableShell>
          <TableHeadRow columns={COLS}>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Serial · model</TableHeadCell>
            <TableHeadCell>Group</TableHeadCell>
            <TableHeadCell>Site</TableHeadCell>
            <TableHeadCell>IP / MAC</TableHeadCell>
            <TableHeadCell />
          </TableHeadRow>
          {visible.map((row) => (
            <TableRow key={row.serial} columns={COLS}>
              <TableCell>
                <div className="truncate text-sm font-semibold text-fg">
                  {row.name ?? "(unnamed)"}
                </div>
                {row.firmware ? (
                  <div className="font-mono text-[10.5px] text-fg-mute">
                    fw {row.firmware}
                  </div>
                ) : null}
              </TableCell>
              <TableCell>{typeChip(row.deviceType)}</TableCell>
              <TableCell>{statusBadge(row.status)}</TableCell>
              <TableCell>
                <div className="font-mono text-[11px] text-fg">
                  {row.serial}
                </div>
                <div className="font-mono text-[10.5px] text-fg-mute">
                  {row.model ?? "—"}
                </div>
              </TableCell>
              <TableCell className="truncate text-xs text-fg-dim">
                {row.group ?? (
                  <span className="text-fg-mute">(unassigned)</span>
                )}
              </TableCell>
              <TableCell className="truncate text-xs text-fg-dim">
                {row.site ?? <span className="text-fg-mute">(no site)</span>}
              </TableCell>
              <TableCell className="font-mono text-[10.5px] text-fg-dim">
                <div>{row.ipAddress ?? "—"}</div>
                <div className="text-fg-mute">{row.macAddress ?? "—"}</div>
              </TableCell>
              <TableCell className="flex justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => diagnose(row)}
                  title="Ask the agent to diagnose this device"
                >
                  <Stethoscope size={11} />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-fg-mute">
              {query || activeTypes.size > 0
                ? "No devices match your filter."
                : "No devices in inventory."}
            </div>
          ) : null}
          {overflow > 0 ? (
            <div className="border-t border-line bg-surface-tint/40 px-4 py-2 text-center text-[11px] text-fg-mute">
              {overflow.toLocaleString()} more match — refine the filter to see
              them.
            </div>
          ) : null}
        </TableShell>
      )}
    </div>
  );
}
